/** Catalog and room timeline contracts. File locations are resolved by the server. */
import type { RoomMusicDefaults } from './RoomMusicDefaults';
export const MUSIC_DEFAULT_LEVEL = 0.35;
export const MUSIC_START_LEAD_MS = 2000;
/**
 * #4318 — the lead for `next`/`previous`, which move within the queue the client is already
 * warming. The long lead exists to cover fetching a track nobody has warmed; stepping through the
 * existing queue needs no fetch, only delivery. Two seconds of silence before the music answers
 * reads as the room ignoring you.
 *
 * #4291 — the client no longer needs the WHOLE of its crossfade inside this lead: it fits the ramp
 * to the window each listener actually has, so delivery costs a shorter fade rather than a step
 * down into the middle of one. There is still a floor, and it is not this value. The gain is
 * commanded from a 25 ms tick, so what remains after delivery has to be worth rendering as a ramp;
 * below the client's own minimum it splices instead. Cutting this further therefore buys
 * immediacy by giving up the fade, which is a product call (item 3 of #4291), not a free change.
 */
export const MUSIC_SKIP_LEAD_MS = 500;
export const MUSIC_PAUSE_LEAD_MS = 250;
export const MUSIC_TRACK_GAP_MS = 250;
export const MUSIC_SESSION_TTL_SECONDS = 4 * 60 * 60;
export const MUSIC_MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;
export const MUSIC_GENRES = ['Ambient', 'Chill', 'Classical', 'Electronic', 'Jazz', 'Lo-fi', 'Pop', 'Rock', 'Soul', 'World'] as const;

export interface MusicPortability {
  verdict: 'passes' | 'warnings' | 'refused';
  warnings: string[];
  reasons: string[];
}

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  album: string | null;
  year: number | null;
  genreTags: string[];
  durationMs: number;
  url: string;
  coverUrl: string | null;
  gainDb: number;
  hasVocals: boolean;
  explicit: boolean;
  /**
   * The version of this track's song card (#4279); absent when it has none. A room is handed the
   * VERSION, never the card: the queue is written to Redis, broadcast to every client, validated by
   * the feeder and published to Sixync, and must grow by a few bytes per track, not by kilobytes.
   * The card itself is fetched by track id and version, and is immutable under it.
   */
  cardVersion?: number;
}

export interface MusicPlaylistSummary {
  id: string;
  slug: string;
  title: string;
  genre: string;
  description: string | null;
  coverUrl: string | null;
  trackCount: number;
  durationMs: number;
  /**
   * Present only on a mix made by request (#4281, #4282; FunctionalSpec §8.2, §9.4): the words it was made from. `title` is then
   * the mix's short label. A stored playlist has none, so a room state without it is unchanged. (Not a `kind`: a stored
   * playlist's row already has one, meaning something else.)
   */
  request?: string;
}

export interface MusicPlaylist extends MusicPlaylistSummary {
  tracks: MusicTrack[];
}

/** Source kinds leave room for timestamped live sources without changing the clock. */
export type MusicSourceKind = 'catalog' | 'live';
export interface MusicTransport {
  status: 'playing' | 'paused' | 'off';
  pauseReason: 'host' | 'yielded' | null;
  currentIndex: number;
  virtualStartAtMs: number | null;
  pausedPositionMs: number;
  effectiveAtMs: number;
  next: { index: number; startAtMs: number } | null;
}

export interface RoomMusicState extends MusicTransport {
  defaults?: RoomMusicDefaults;
  /** Persists across reconnects; cleared only with the room session. */
  defaultsInitialized?: boolean;
  roomId: string;
  revision: number;
  sourceKind: MusicSourceKind;
  playlist: MusicPlaylistSummary | null;
  queue: MusicTrack[];
  /** Retain the outgoing transport until a scheduled command takes effect. */
  previous: (MusicTransport & {
    queue: MusicTrack[];
    /** Outgoing labels stay with outgoing audio until the command deadline.
     * Optional for snapshots written before playlist metadata was retained. */
    playlist?: MusicPlaylistSummary | null;
  }) | null;
  level: number;
  /**
   * Whether the room's order is SHUFFLED (#4347). Absent means it is not — snapshots written before this outlive it
   * in Redis for the session TTL, and a room that has never shuffled should not carry the field.
   *
   * It is a property of the ORDER, not a coin flipped at each track boundary. Playback here is a pure projection every
   * client computes for itself from `virtualStartAtMs` and the queue — `projectMusicTransport` walks `(index + 1) %
   * length` — so a per-advance random pick cannot exist: two listeners would draw different tracks, and a read after
   * an outage could not skip whole cycles. Shuffle therefore permutes the queue ONCE, when it is switched on and
   * again for each playlist chosen while it is on, and the timeline underneath is untouched.
   */
  shuffle?: boolean;
  /**
   * The ids of `queue`, in the order it held before it was shuffled, so switching shuffle off puts the room back
   * rather than leaving it scrambled with an unlit button. Ids, not tracks: a second copy of the queue would double
   * what every state write and broadcast carries, for a permutation of what is already there. Absent whenever the
   * order is the playlist's own, and dropped with the queue it describes.
   */
  unshuffled?: string[];
  includeInProgram: boolean;
  programAlignmentMs: number;
  yieldedBy: string[];
  lastCommandBy: string | null;
  /**
   * How many commands have changed WHAT PLAYS in this session (#4282): play, pause, skip, shuffle, off, a playlist, a mix.
   * Not the level or the recording switch, not a track ending, not music yielding to a video — none of which is someone
   * deciding what the room hears. A request for a mix takes seconds to make and lands without holding the room; this is
   * how it knows, on any worker, that someone has decided otherwise since it was asked for. Absent means none yet.
   */
  commandSeq?: number;
  /**
   * The mix that made the LAST of those commands: when it was asked for, and what the count above stood at when it was —
   * present only while that last command is a mix (#4282). Two mixes can be in the making at once, and the one asked for
   * first may be the slower. Which of them the room keeps is decided by when each was ASKED, as it is for two stored
   * playlists chosen a second apart, and never by which was quicker to make: a mix gives way to a later ask that landed
   * first, and lands over an earlier ask that landed meanwhile — but only over one that had seen no more of the room's
   * commands than it had itself, so that an "off" said in between is never undone through it.
   */
  mixAsked?: { atMs: number; seq: number };
  serverTimeMs: number;
}

export type MusicStateEvent = Omit<RoomMusicState, 'queue'> & { queue?: MusicTrack[] };

export interface RoomClockExchange {
  clientSentMs: number;
  serverReceivedMs: number;
  serverSentMs: number;
}

export interface MusicSyncReport {
  revision: number;
  medianErrorMs: number;
  p95ErrorMs: number;
  outputLatencyKnown: boolean;
  deviceClass: string;
  sampleCount: number;
}
